import type { ReactNode } from "react";

import type { OrientationLetters } from "@/lib/viewer/room";

/** A reference line on this cell, in its canvas's CSS pixels. */
export interface CellLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  colour: string;
}

/**
 * The chrome drawn over one viewport cell: the four corners, the edge
 * letters and the label pill. The same for a series cell and a reformat
 * plane, so the two read as one viewer rather than two.
 *
 * Everything here is `pointer-events: none` except the cell itself,
 * whose mousedown makes it the ACTIVE cell -- the one the toolbar, the
 * keys and the slider act on, as the design's toolbar map says. So does
 * the wheel: the cell being scrolled is the one sync and the reference
 * lines should follow, not one clicked a minute ago.
 */
export function RoomCell({
  label,
  colour,
  active,
  framed,
  overlays,
  letters,
  corners,
  note,
  line,
  cy,
  onActivate,
  children,
}: {
  label: string;
  colour: string;
  active: boolean;
  /** Whether the active cell is marked at all: on a single cell there is
   *  nothing to tell it apart from. */
  framed: boolean;
  overlays: boolean;
  letters: OrientationLetters | null;
  corners: { tl?: ReactNode; tr?: ReactNode; bl?: ReactNode; br?: ReactNode };
  note?: string | null;
  line?: CellLine | null;
  cy: string;
  onActivate?: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="rr-cell"
      data-cy={cy}
      data-active={active || undefined}
      style={{ borderColor: framed && active ? "#5cc8b8" : "#1f252d" }}
      onMouseDownCapture={onActivate}
      onWheelCapture={onActivate}
    >
      {children}
      {line && (
        <svg className="rr-refline" aria-hidden="true" data-cy="rr-refline">
          <line x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke={line.colour} strokeWidth={1} opacity={0.9} />
        </svg>
      )}
      {overlays && (
        <>
          {corners.tl && <div className="rr-corner rr-corner--tl">{corners.tl}</div>}
          {corners.tr && <div className="rr-corner rr-corner--tr">{corners.tr}</div>}
          {corners.bl && <div className="rr-corner rr-corner--bl">{corners.bl}</div>}
          {corners.br && <div className="rr-corner rr-corner--br">{corners.br}</div>}
          {letters && (
            <>
              <span className="rr-letter rr-letter--t" data-cy="rr-letter-top">{letters.top}</span>
              <span className="rr-letter rr-letter--b" data-cy="rr-letter-bottom">{letters.bottom}</span>
              <span className="rr-letter rr-letter--l" data-cy="rr-letter-left">{letters.left}</span>
              <span className="rr-letter rr-letter--r" data-cy="rr-letter-right">{letters.right}</span>
            </>
          )}
        </>
      )}
      <div className="rr-label-pill" title={note ? `${label} — ${note}` : label}>
        <span className="rr-dot" style={{ background: colour, flex: "none" }} />
        <span className="rr-pill-text">{label}</span>
        {note && <span className="rr-pill-note">{note}</span>}
      </div>
    </div>
  );
}
